import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { PageShell } from "../../shared/layouts/PageShell";
import { Breadcrumbs } from "../../../components/Breadcrumbs";
import type { BreadcrumbItem } from "../../../components/Breadcrumbs";
import { PageHeader } from "../../shared/ui/layouts/PageHeader";
import { SurfaceCard } from "../../shared/ui/surface/SurfaceCard";
import { MetricTile } from "../../shared/ui/MetricTile";
import { mapStyles } from "../../../components/map/createBaseMap";
import { MapShell } from "../../../components/map/MapShell";
import { LotVerticesMap, type LotVerticesMapStatus } from "../../../components/map/LotVerticesMap";
import { mapFrameClasses } from "../../../components/map/mapFrameClasses";
import { useThemeMode } from "../../../providers/ThemeProvider";
import { useBreadcrumbBack } from "../../../components/useBreadcrumbBack";
import { getPublicLot, type PublicLot } from "../api/publicLots";
import { useAnalytics } from "../../../hooks/useAnalytics";
import { defaultIconProps } from "../../shared/ui/icons";
import { MapPin, ArrowLeft, Ruler } from "@phosphor-icons/react";
import { useMapExpansion } from "../../../components/map/useMapExpansion";
import type { Map as MapInstance } from "maplibre-gl";

const pageStackClasses = "flex flex-col gap-6 text-slate-900 dark:text-slate-100";
const contentGridClasses = "grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]";
const cardTitleClasses = "text-base font-semibold text-slate-900 dark:text-slate-100";
const listItemClasses = "flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200";

const uuidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

export function PublicLotPage() {
  const { slugId } = useParams<{ slugId: string }>();
  const { t } = useTranslation();
  const { theme } = useThemeMode();
  const { track } = useAnalytics();
  const {
    isExpanded: isMapExpanded,
    toggle: toggleMapExpansion,
  } = useMapExpansion();
  const [lot, setLot] = useState<PublicLot | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mapStatus, setMapStatus] = useState<LotVerticesMapStatus>({
    isMapReady: false,
    hasRenderedTiles: false,
  });
  const mapInstanceRef = useRef<MapInstance | null>(null);

  const breadcrumbItems = useMemo<BreadcrumbItem[]>(() => {
    return [{ label: t("public_lot.breadcrumb", "Public property") }];
  }, [t]);
  const handleBack = useBreadcrumbBack({
    items: breadcrumbItems,
    fallback: { to: "/" },
  });

  const lotId = useMemo(() => {
    const source = slugId ?? "";
    const match = source.match(uuidRegex);
    return match ? match[0] : source;
  }, [slugId]);

  const entrypoint = useMemo(() => {
    if (typeof document === "undefined") {
      return "direct";
    }
    const referrer = document.referrer || "";
    if (!referrer) {
      return "direct";
    }
    try {
      const refUrl = new URL(referrer);
      const host = typeof window !== "undefined" ? window.location.host : "";
      if (host && refUrl.host === host) {
        return "direct";
      }
      if (/google|bing|yahoo|duckduckgo|search/i.test(refUrl.host)) {
        return "seo";
      }
      return "share";
    } catch {
      return "direct";
    }
  }, []);

  const formatNumber = useMemo(
    () =>
      new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!lotId) {
        setError(t("public_lot.not_found"));
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const data = await getPublicLot(lotId);
        if (cancelled) return;
        setLot(data);
      } catch {
        if (cancelled) return;
        setError(t("public_lot.load_error"));
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [lotId, t]);

  useEffect(() => {
    if (!lot?.id) {
      return;
    }
    track({
      feature: "marketplace",
      action: "marketplace.listing_public.viewed",
      resourceType: "listing",
      resourceId: lot.id,
      properties: {
        listing_id: lot.id,
        status: lot.status,
        entrypoint,
        page: "public_profile",
        referrer: typeof document !== "undefined" ? document.referrer || undefined : undefined,
      },
    });
  }, [entrypoint, lot?.id, lot?.status, track]);

  const title = lot?.title ?? t("public_lot.title");
  const areaDisplay =
    lot?.area_sqm && Number.isFinite(lot.area_sqm)
      ? `${formatNumber.format(lot.area_sqm)} m²`
      : t("common.table.empty_value");

  const locationParts = [lot?.barangay, lot?.municipality, lot?.province, lot?.region].filter(
    (v): v is string => Boolean(v),
  );
  const locationLabel = locationParts.length > 0 ? locationParts.join(", ") : t("public_lot.location_unknown", "Philippines");

  const overlayVisibility = useMemo(
    () => ({
      angles: false,
      lengths: true,
      corners: false,
      cornerMarkers: false,
    }),
    [],
  );

  const styleOverrides = useMemo(
    () => ({
      fillColor: "#3b82f6",
      fillOpacity: 0.28,
      outlineColor: "rgba(59, 130, 246, 0.9)",
      outlineWidth: 2,
      lengthTextColor: "#0f172a",
      lengthTextHaloColor: "rgba(255,255,255,0.96)",
    }),
    [],
  );

  const segmentLengths = (lot?.segments ?? []).filter(
    (segment) => !segment.is_tie && segment.seq_no > 0,
  );

  const mapCanvasClasses = useMemo(
    () =>
      [
        mapFrameClasses.canvas,
        "transition-opacity duration-500",
        isMapExpanded ? mapFrameClasses.canvasExpanded : "",
        mapStatus.hasRenderedTiles ? "opacity-100" : "opacity-0",
      ]
        .filter(Boolean)
        .join(" "),
    [isMapExpanded, mapStatus.hasRenderedTiles],
  );

  const handleZoom = useCallback(
    (delta: number) => {
      const map = mapInstanceRef.current;
      if (!map) return;
      const zoom = map.getZoom();
      if (!Number.isFinite(zoom)) return;
      map.easeTo({ zoom: zoom + delta, duration: 200 });
    },
    [],
  );

  return (
    <PageShell width="wide">
      <div className={pageStackClasses}>
        <Breadcrumbs items={breadcrumbItems} />
        <PageHeader
          title={title}
          subtitle={t("public_lot.subtitle")}
          contentClassName="max-w-3xl"
          actions={
            <button type="button" className="pm-btn pm-btn-ghost pm-btn-sm" onClick={handleBack}>
              <ArrowLeft {...defaultIconProps} aria-hidden />
              {t("actions.back")}
            </button>
          }
        />

        {error ? (
          <SurfaceCard tone="muted">
            <SurfaceCard.Body>{error}</SurfaceCard.Body>
          </SurfaceCard>
        ) : null}

        <div className="flex flex-wrap gap-3">
          <MetricTile
            icon={<MapPin {...defaultIconProps} aria-hidden />}
            label={t("public_lot.location_label")}
            value={locationLabel}
          />
          <MetricTile
            icon={<Ruler {...defaultIconProps} aria-hidden />}
            label={t("public_lot.area_label")}
            value={areaDisplay}
          />
        </div>

        <div className={contentGridClasses}>
          <SurfaceCard tone="interactive">
            <SurfaceCard.Body gap="tight">
              <div className="flex items-center justify-between">
                <h2 className={cardTitleClasses}>{t("public_lot.map_heading")}</h2>
                {loading ? <span className="text-sm text-slate-500 dark:text-slate-400">{t("common.loading")}</span> : null}
              </div>
              <MapShell
                mapContent={
                  <LotVerticesMap
                    className={mapCanvasClasses}
                    mapStyle={theme === "dark" ? mapStyles.dark : mapStyles.light}
                    vertices={lot?.vertices ?? []}
                    segments={lot?.segments ?? []}
                    overlayVisibility={overlayVisibility}
                    fitPadding={48}
                    boundsMaxZoom={21}
                    styleOverrides={styleOverrides}
                    onStatusChange={(next) => setMapStatus(next)}
                    mapRef={mapInstanceRef}
                  />
                }
                status={mapStatus}
                loadingLabel={t("workspaces.lots.map.loading_map", "Preparing map…")}
                isExpanded={isMapExpanded}
                onToggle={toggleMapExpansion}
                toggleLabels={{
                  expandButton: t("workspaces.lot_detail.map_expand_button"),
                  collapseButton: t("workspaces.lot_detail.map_collapse_button"),
                  expandAria: t("workspaces.lot_detail.map_expand_aria"),
                  collapseAria: t("workspaces.lot_detail.map_collapse_aria"),
                }}
                fabAriaLabel={t("workspaces.lot_detail.map_controls_aria")}
                onZoomIn={() => handleZoom(1)}
                onZoomOut={() => handleZoom(-1)}
                zoomAria={{
                  in: t("workspaces.lots.map.zoom_in", "Zoom in"),
                  out: t("workspaces.lots.map.zoom_out", "Zoom out"),
                }}
              />
            </SurfaceCard.Body>
          </SurfaceCard>

          <SurfaceCard tone="muted">
            <SurfaceCard.Body gap="tight">
              <h2 className={cardTitleClasses}>{t("public_lot.segment_lengths.title")}</h2>
              {segmentLengths.length === 0 ? (
                <p className="text-sm text-slate-600 dark:text-slate-400">{t("public_lot.segment_lengths.empty")}</p>
              ) : (
                <div className="flex flex-col gap-2">
                  {segmentLengths.map((segment, idx) => (
                    <div key={idx} className={listItemClasses}>
                      <span className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        {t("public_lot.segment_lengths.label", { index: idx + 1 })}
                      </span>
                      <span>
                        {t("public_lot.segment_lengths.meters", {
                          length: formatNumber.format(segment.distance_m),
                        })}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {t("public_lot.segment_lengths.disclaimer")}
              </p>
            </SurfaceCard.Body>
          </SurfaceCard>
        </div>

        <div className="text-sm text-slate-500 dark:text-slate-400">
          {t("public_lot.updated_at", { date: lot?.updated_at ? new Date(lot.updated_at).toLocaleDateString() : "—" })}
        </div>
      </div>
    </PageShell>
  );
}
